import * as React from "react";
import {css, cx} from "emotion";
import {rgba} from "polished";

import "./paper-pricing-table.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {UseState}       from "@Common/react/use-state";
import {isDevQuan}      from "@Common/ui-components/debug/is-dev-quan";
import {formatMoney}    from "@Common/ui-components/language/format-money";
import {sort}           from "@Common/utils/collections";
import {chain}          from "@Common/utils/fs";
import {values}         from "@Common/utils/objects";

export const PaperPricingTable = (
    {
        deliveryPaperTypes,
        defaultViewAllPrices,
        paperType,
        focusedDelivery,
        isNDM,
        hasEddm,
        hidePaperTypes,
        expanded,
        eddmPrice
    }
) => cs(
    ({}, next) => !paperType.productPrice ? null : next(),
    ({}, next) => (
        <div className="paper-pricing-table-4rm">
            {next()}
        </div>
    ),
    ["viewAll", (_, next) => UseState({next})],
    ({viewAll}) => <>
        {hidePaperTypes && !isDevQuan() ? (
            null
        ) : focusedDelivery.type !== "download" && (
            TitleBar({
                label: paperType.name,
                xdQaName: paperType.name,
                expanded: {
                    ...expanded,
                    onChange: (v) => {
                        expanded.onChange(v);
                        viewAll.onChange(false);
                    }
                },
                count: deliveryPaperTypes.length
            })
        )}

        {(defaultViewAllPrices || expanded.value) && Table({
            cols: [
                focusedDelivery.type !== "download" && {
                    label: "QUANTITY",
                    render: ({samplePrice}) => !samplePrice.isFlatRate ? `${samplePrice.min} - ${samplePrice.max}` : samplePrice.min,
                },
                {
                    label: "PRICE",
                    render: ({samplePrice}) => formatMoney1(samplePrice.price)
                },
                focusedDelivery.subType === "3" && hasEddm && {
                    label: "EDDM ELITE",
                    render: ({samplePrice}) => samplePrice.price != null && formatMoney(samplePrice.price - (eddmPrice ? eddmPrice : eddmOffset))
                }
            ].filter(v => v),

            rows: (() => {
                const pricing = ["1", "3", "5"].includes(focusedDelivery.subType) ? (
                    paperType.productPrice["mailing"][focusedDelivery.subType]
                ) : (
                    paperType.productPrice[focusedDelivery.type]
                );

                return chain(
                    pricing ?? {},
                    (pricing) => values(pricing),
                    (samplePrices) => samplePrices.map((price, index) => ({price, index})),
                    (samplePrices) => sort(samplePrices, (price) => price.price.min),
                    (samplePrices) => (defaultViewAllPrices || viewAll.value) ? samplePrices : samplePrices.slice(0, 3),
                    (samplePrices) => samplePrices.map(({price, index}) => ({
                        samplePrice: price,
                    })),
                );

            })(),

            footerCaption: (
                focusedDelivery.type !== "download" &&
                !defaultViewAllPrices &&
                !viewAll.value
            ) && {
                className: "show-more-row",
                onClick: () => viewAll.onChange(true),
                content: "+ View More Tiers",
            },
        })}
    </>
);

const formatMoney1 = (v) => v != null && formatMoney(v);

const eddmOffset = 0.06;

const Table = ({cols, rows, footerCaption}) => cs(
    consumeContext("themeScheme"),
    ({themeScheme}) => (
        <div className="table">
            {rows.length ? <>
                <div
                    className={cx(
                        "row head-row",
                        css`
                        &:nth-child(even) {
                            background: ${rgba(themeScheme.xpresso.menuBackgroundColor, 0.1)};
                        }
                        `
                    )}
                >
                    {cols.map((col, i) => (
                        <div className="cell" key={i} xd-qa-type="text" xd-qa-name={col.label}>
                            {col.label}
                        </div>
                    ))}
                </div>
                {rows.map((row, rowIndex) => (
                    <div
                        className={cx(
                            "row body-row",
                            css`
                            &:nth-child(even) {
                                background: ${rgba(themeScheme.xpresso.menuBackgroundColor, 0.1)};
                            }
                            `
                        )}
                        key={rowIndex}
                    >
                        {cols.map((col, i) => (
                            <div className="cell" key={i} xd-qa-type="text" xd-qa-name={getQaAttributeName({col, row})}>
                                {col.render(row)}
                            </div>
                        ))}
                    </div>
                ))}
            </> : (
                <div className="error">
                    No pricing available
                </div>
            )}

            {footerCaption && (
                <div
                    className={cx(
                        "footer-caption",
                        footerCaption.className,
                        css`
                          &:nth-child(even) {
                            background: ${rgba(themeScheme.xpresso.menuBackgroundColor, 0.1)};
                          }
                        `
                    )}
                    onClick={footerCaption.onClick}
                    xd-qa-type="label"
                    xd-qa-name={footerCaption.content}
                >
                    {footerCaption.content}
                </div>
            )}
        </div>
    )
);

const TitleBar = ({label, xdQaName, expanded, count}) => (
    <div
        className="title"
        onClick={() => expanded.onChange(!expanded.value)}
        xd-qa-type="button"
        xd-qa-name={xdQaName}
    >
        {label}

        {count > 1 && (
            expanded.value ? (
                <i className="fa fa-chevron-up"/>
            ) : (
                <i className="fa fa-chevron-down"/>
            )
        )}
    </div>
);

const getQaAttributeName = ({col, row}) => {
    if(col.label === "QUANTITY") {
        return col.render(row)
    }
    if(col.label === "PRICE") {
        return typeof(col.render(row)) === "string" ? col.render(row) : row.samplePrice.price * 100 + '¢'
    }
    if(col.label === "EDDM ELITE") {
        return typeof(col.render(row)) === "string" ? col.render(row) : ((row.samplePrice.price - eddmOffset) * 100) + '¢'
    }
}